import { Field, FieldInputProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppMyAccountCard from "@/features/my-account/app-my-account-card";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useUpdateUser from "@/features/my-account/hooks/use-update-user";
import { useEffect, useRef } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";

interface UpdateUserFormValues {
  name: string;
}

export default function UserCard() {
  const identity = useIdentity();
  const userId = identity?.id;
  const { t, tTitle, tSentence } = useI18nHelper();
  const { withMessage, formT } = useYupHelper({
    prefix: "user_form",
    ns: "my-account",
  });
  const { isLoading: isUserLoading, data: user, error } = useGetMySelf();
  const { error: showError } = useAppToasts({ id: "GLOBAL" });

  const {
    isLoading: isUpdateUserLoading,
    error: updateUserError,
    fire: fireUpdateUser,
  } = useUpdateUser();

  const formikRef = useRef<FormikProps<UpdateUserFormValues> | null>(null);


  useEffect(() => {
    if (user && formikRef.current && formikRef.current?.dirty === false) {
      formikRef.current?.setValues({
        name: user.name,
      });
    }
  }, [user]);

  return (
    <AppMyAccountCard title={tTitle("common:profile")}>
      <Formik
        initialValues={{
          name: "",
        }}
        validationSchema={Yup.object({
          name: Yup.string().required(withMessage.required),
        })}
        onSubmit={(values, actions) => {
          if (!isUserLoading && userId) {
            fireUpdateUser(
              {
                userId: userId,
              },
              {
                name: values.name!,
              }
            );
          }
          actions.setSubmitting(false);
        }}
        innerRef={formikRef}
      >
        {(props) => {
          return (
            <Form>
              <Field name="name">
                {({
                  field,
                  form,
                }: {
                  field: FieldInputProps<string | undefined>;
                  form: FormikProps<UpdateUserFormValues>;
                }) => (
                  <FormControl
                    isRequired
                    isInvalid={!!form.errors.name && form.touched.name}
                  >
                    <Grid
                      // 320px 인풋 + 88px 버튼 + 8px 간격이면 416px이 필요해 모바일 카드
                      // 내부 폭을 넘긴다. 모바일에서는 인풋만 남는 폭을 쓰고 버튼 폭은 지킨다.
                      gridTemplateColumns={{
                        base: "1fr 88px",
                        md: "320px 88px",
                      }}
                      // 라벨·인풋 행 높이를 고정하면 좁은 폭에서 줄바꿈된 글자가 겹친다.
                      gridTemplateRows={{ base: "auto auto", md: "16px 24px" }}
                      rowGap={"4px"}
                      columnGap={"8px"}
                      position={"relative"}
                    >
                      <FormLabel display="flex" m={"0"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle("name")}
                        </Text>
                      </FormLabel>
                      <Box></Box>
                      <Input
                        {...field}
                        fontSize={"12px"}
                        minHeight={"0"}
                        height={"28px"}
                      />
                      <AppButton
                        variant={"primary"}
                        size={"medium"}
                        type={"submit"}
                        isLoading={isUpdateUserLoading || props.isSubmitting}
                        isDisabled={!(props.isValid && props.dirty)}
                        onClick={(event) => {
                          //event.preventDefault();
                        }}
                      >
                        {tTitle("common:save")}
                      </AppButton>
                      <Box position={"absolute"} bottom={"-16px"}>
                        <FormErrorMessage m="0" fontSize={"12px"}>
                          {form.errors.name}
                        </FormErrorMessage>
                      </Box>
                    </Grid>
                  </FormControl>
                )}
              </Field>
            </Form>
          );
        }}
      </Formik>
    </AppMyAccountCard>
  );
}
